/**
 * matte-store.js — holds the per-frame mattes for a clip, and the small source
 * thumbnails the review phase shows beside them.
 *
 * WHAT IS STORED, PER FRAME
 *   alpha   the matte itself, one byte per pixel, DEFLATED (CompressionStream).
 *           A matte is mostly runs of 0 and 255, so this is a few tens of KB
 *           at 1080p.
 *   fg      the straight foreground RGB as a JPEG (q 0.85). A photo does not
 *           compress losslessly — that is the whole story below — and JPEG at
 *           this quality is far above what any of the video codecs we export
 *           to will keep.
 *   thumb   a 240-px JPEG of the SOURCE frame, so the review phase can show a
 *           synchronised before/after instead of the matte alone. Optional:
 *           put() stores it only when handed the source canvas.
 *
 * WHY NOT ONE PNG PER FRAME (what this file did until 2026-09-02). The RGB
 * channel of a matte is a photograph, and PNG's deflate does nothing for a
 * photograph: measured 657 KB per frame at 720p and 710 KB at 1080p — the
 * "~100–150 MB for 30 s of 1080p" this header used to promise was written for
 * flat regions that a real foreground does not have. At the Premium ceiling
 * (60 s · 30 fps · 1080p = 1800 frames) that is 1.28 GB of blobs, which is not
 * a slow export but a dead tab: the phone budget is 500–700 MB for the whole
 * page. The PNG encode was also the single largest cost of the matte pass —
 * 73–117 ms per 1080p frame, more than the colour key (59 ms) or the WebGPU
 * network — so splitting the channels is faster as well as smaller.
 *
 * DECODE. get() rebuilds the RGBA canvas: draw the JPEG, inflate the alpha,
 * write it into the alpha channel. Decoded canvases sit in a small LRU that is
 * bounded in PIXELS, not entries: sixteen 1080p canvases are 130 MB, sixteen
 * 360p canvases are 6 MB, and only one of those is a budget.
 *
 * Public API — window.VbrMatteStore:
 *   init(meta)                    meta: { frameCount, width, height, fps,
 *                                         trimStart, trimEnd, fileName }
 *   put(index, canvas, src?)      → Promise<void>   encode + store (+ thumbnail)
 *   get(index)                    → Promise<canvas> decoded matte (LRU)
 *   getThumb(index)               → Promise<ImageBitmap|null>  source thumbnail
 *   has(index)                    → bool
 *   count()                       → number of stored mattes
 *   meta()                        → the meta object
 *   bytes()                       → total stored bytes (alpha + fg + thumbs)
 *   clear()
 */
(function (root) {
    'use strict';

    var LRU_PIXEL_BUDGET = 12 * 1024 * 1024;   // ≈ 48 MB of RGBA canvases
    var JPEG_Q = 0.85;
    var THUMB_W = 240;
    var THUMB_Q = 0.7;

    var frames = new Map();       // index → { alpha: Uint8Array (deflated), fg: Blob, thumb: Blob|null }
    var lru = new Map();          // index → canvas (insertion-ordered)
    var lruPixels = 0;
    var totalBytes = 0;
    var info = null;

    var scratch = null, scratchCtx = null;     // reused for JPEG encodes
    var thumbC = null, thumbCtx = null;

    function init(meta) {
        clear();
        info = Object.assign({
            frameCount: 0, width: 0, height: 0, fps: 30,
            trimStart: 0, trimEnd: 0, fileName: 'video',
        }, meta || {});
    }

    /* ── codecs ───────────────────────────────────────────────────────── */

    var canDeflate = typeof CompressionStream === 'function' && typeof DecompressionStream === 'function';

    async function deflate(bytes) {
        if (!canDeflate) return bytes;
        var cs = new CompressionStream('deflate-raw');
        var w = cs.writable.getWriter();
        w.write(bytes); w.close();
        return new Uint8Array(await new Response(cs.readable).arrayBuffer());
    }
    async function inflate(bytes) {
        if (!canDeflate) return bytes;
        var ds = new DecompressionStream('deflate-raw');
        var w = ds.writable.getWriter();
        w.write(bytes); w.close();
        return new Uint8Array(await new Response(ds.readable).arrayBuffer());
    }

    function toBlob(canvas, type, q) {
        return new Promise(function (resolve, reject) {
            if (canvas.convertToBlob) {
                canvas.convertToBlob({ type: type, quality: q }).then(resolve, reject);
            } else {
                canvas.toBlob(function (b) { b ? resolve(b) : reject(new Error(type + ' encode failed')); }, type, q);
            }
        });
    }

    function ensureScratch(w, h) {
        if (!scratch) {
            scratch = document.createElement('canvas');
            scratchCtx = scratch.getContext('2d', { alpha: false, willReadFrequently: true });
        }
        if (scratch.width !== w || scratch.height !== h) { scratch.width = w; scratch.height = h; }
    }

    /* ── write ────────────────────────────────────────────────────────── */

    async function put(index, canvas, src) {
        var W = canvas.width, H = canvas.height;
        var d = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data;

        // alpha → one byte per pixel → deflate
        var a = new Uint8Array(W * H);
        for (var i = 3, j = 0; i < d.length; i += 4, j++) a[j] = d[i];
        var alphaP = deflate(a);

        // foreground → opaque canvas → JPEG. Drawing the RGBA canvas onto an
        // opaque one would composite over black and darken every soft edge, so
        // the bytes are copied with alpha forced to 255 instead.
        ensureScratch(W, H);
        var opaque = scratchCtx.createImageData(W, H);
        var od = opaque.data;
        od.set(d);
        for (var k = 3; k < od.length; k += 4) od[k] = 255;
        scratchCtx.putImageData(opaque, 0, 0);
        var fgP = toBlob(scratch, 'image/jpeg', JPEG_Q);

        // thumbnail of the SOURCE, when the pass hands it over
        var thumbP = Promise.resolve(null);
        if (src && src.width && src.height) {
            var tw = Math.min(THUMB_W, src.width);
            var th = Math.max(2, Math.round(tw * src.height / src.width));
            if (!thumbC) { thumbC = document.createElement('canvas'); thumbCtx = thumbC.getContext('2d', { alpha: false }); }
            if (thumbC.width !== tw || thumbC.height !== th) { thumbC.width = tw; thumbC.height = th; }
            thumbCtx.drawImage(src, 0, 0, tw, th);
            thumbP = toBlob(thumbC, 'image/jpeg', THUMB_Q).catch(function () { return null; });
        }

        var res = await Promise.all([alphaP, fgP, thumbP]);
        var entry = { alpha: res[0], fg: res[1], thumb: res[2], w: W, h: H };
        if (frames.has(index)) totalBytes -= entryBytes(frames.get(index));
        frames.set(index, entry);
        totalBytes += entryBytes(entry);
    }

    function entryBytes(e) {
        return (e.alpha ? e.alpha.byteLength : 0) + (e.fg ? e.fg.size : 0) + (e.thumb ? e.thumb.size : 0);
    }

    /* ── read ─────────────────────────────────────────────────────────── */

    function lruTouch(index, canvas) {
        if (lru.has(index)) { lruPixels -= lru.get(index).width * lru.get(index).height; lru.delete(index); }
        lru.set(index, canvas);
        lruPixels += canvas.width * canvas.height;
        while (lru.size > 1 && lruPixels > LRU_PIXEL_BUDGET) {
            var oldest = lru.keys().next().value;
            var c = lru.get(oldest);
            lruPixels -= c.width * c.height;
            lru.delete(oldest);
        }
    }

    async function get(index) {
        if (lru.has(index)) {
            var hit = lru.get(index);
            lru.delete(index); lru.set(index, hit);
            return hit;
        }
        var e = frames.get(index);
        if (!e) return null;

        var bmpP = createImageBitmap(e.fg);
        var alphaP = inflate(e.alpha);
        var bmp = await bmpP;
        var c = document.createElement('canvas');
        c.width = e.w; c.height = e.h;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(bmp, 0, 0);
        bmp.close();
        var img = ctx.getImageData(0, 0, e.w, e.h);
        var d = img.data, a = await alphaP;
        for (var i = 3, j = 0; i < d.length; i += 4, j++) d[i] = a[j];
        ctx.putImageData(img, 0, 0);
        lruTouch(index, c);
        return c;
    }

    async function getThumb(index) {
        var e = frames.get(index);
        if (!e || !e.thumb) return null;
        try { return await createImageBitmap(e.thumb); } catch (_) { return null; }
    }

    function clear() {
        frames.clear();
        lru.clear();
        lruPixels = 0;
        totalBytes = 0;
        info = null;
    }

    root.VbrMatteStore = {
        init: init,
        put: put,
        get: get,
        getThumb: getThumb,
        has: function (i) { return frames.has(i); },
        count: function () { return frames.size; },
        meta: function () { return info; },
        bytes: function () { return totalBytes; },
        clear: clear,
    };
})(typeof window !== 'undefined' ? window : globalThis);
